import React, { useEffect, useState } from 'react';

import Button from './Button';

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    onConfirm,
    onCancel,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmButtonType = 'primary',
    className = '',
    size = 'default',
}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            setTimeout(() => setIsVisible(false), 300);
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose?.();
        }
    };

    const handleConfirm = async () => {
        if (onConfirm) {
            try {
                await onConfirm();
            } catch (error) {
                // If onConfirm throws an error, don't close the modal
                return;
            }
        }
        onClose?.();
    };

    const handleCancel = () => {
        onCancel?.();
        onClose?.();
    };

    const sizeClasses = {
        small: 'max-w-md',
        default: 'max-w-lg',
        large: 'max-w-2xl',
        xlarge: 'max-w-4xl',
    };

    if (!isVisible) return null;

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
                isOpen ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={handleBackdropClick}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50" />

            {/* Modal */}
            <div
                className={`relative bg-white rounded-xl shadow-xl w-full mx-4 transform transition-all duration-300 ${
                    isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
                } ${sizeClasses[size]} ${className}`}
            >
                {/* Header */}
                {title && (
                    <div className="flex items-center justify-between p-6 border-b border-[#d4d9e3]">
                        <h3 className="text-lg font-semibold text-[#101319]">{title}</h3>
                        <button onClick={onClose} className="text-[#5b6b8b] hover:text-[#101319] transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                )}

                {/* Content */}
                <div className="p-6">{children}</div>

                {/* Footer */}
                {(onConfirm || onCancel) && (
                    <div className="flex justify-end gap-3 p-6 border-t border-[#d4d9e3]">
                        {onCancel && (
                            <Button variant="outline" onClick={handleCancel}>
                                {cancelText}
                            </Button>
                        )}
                        {onConfirm && (
                            <Button
                                variant={confirmButtonType === 'danger' ? 'danger' : 'primary'}
                                onClick={handleConfirm}
                            >
                                {confirmText}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// Utility function to create confirmation modals
Modal.confirm = ({ title, content, onOk, onCancel, okText = 'OK', cancelText = 'Cancel', okType = 'primary' }) => {
    return new Promise((resolve) => {
        const modalRoot = document.createElement('div');
        document.body.appendChild(modalRoot);

        const handleOk = async () => {
            try {
                if (onOk) {
                    await onOk();
                }
                cleanup();
                resolve(true);
            } catch (error) {
                // Don't cleanup if onOk throws an error
                throw error;
            }
        };

        const handleCancel = () => {
            if (onCancel) {
                onCancel();
            }
            cleanup();
            resolve(false);
        };

        const cleanup = () => {
            document.body.removeChild(modalRoot);
        };

        const ModalComponent = () => {
            const [isOpen, setIsOpen] = useState(true);

            const handleClose = () => {
                setIsOpen(false);
                setTimeout(handleCancel, 300);
            };

            return (
                <Modal
                    isOpen={isOpen}
                    onClose={handleClose}
                    title={title}
                    onConfirm={handleOk}
                    onCancel={handleCancel}
                    confirmText={okText}
                    cancelText={cancelText}
                    confirmButtonType={okType}
                >
                    {content}
                </Modal>
            );
        };

        // Render the modal
        import('react-dom').then(({ createRoot }) => {
            const root = createRoot(modalRoot);
            root.render(<ModalComponent />);
        });
    });
};

export default Modal;
